import { useEffect, useState, useMemo } from "react";
import { Table, Pagination, Button, Icon, Select, Input, PickListDropdown } from "common-components";

import { Breadcrumb, SearchSelect } from "components";
import { Checkbox, Image, Tag, Spin } from "antd";
import { Loading3QuartersOutlined } from "@ant-design/icons";
import openNotificationWithIcon from "utils/hooks/useNotification";
import { setSelectOptions, getSchoolYear } from "utils/common";
import {
	getCheckQuestion as getCheckQuestionApi,
	updateQuestionCheckStatus as updateQuestionCheckStatusApi,
	createCheckExam as createCheckExamApi,
	downloadCheckQuestions as downloadCheckQuestionsApi,
} from "api/bank";
import {
	getQuerySeparateRecordSelection as getQuerySeparateRecordSelectionApi,
	getQuestionTypeSelection as getQuestionTypeSelectionApi,
} from "api/convert";
import { StyledCheckListPage, UiPopupContainer, StyledAvailability } from "./CheckListPage.style";
import { QuestDetail } from "./QuesDetail";
import { FinishedTaskModal } from "./FinishedTaskModal";
import { UploadModal } from "./UploadModal";

const breadcrumbList = [
	{
		icon: "unarchive",
		name: "快速檢查頁",
		path: "/scheduleList",
	},
];

const filterTypeOptions = [
	{
		name: "待檢查",
		value: 1,
	},
	{
		name: "速測待檢查",
		value: 2,
	},
	{
		name: "紙本待檢查",
		value: 3,
	},
	{
		name: "待教科修題",
		value: 4,
	},
	{
		name: "完成檢查",
		value: 10,
	},
];

const defaultPageState = {
	currentPage: 1,
	limit: 100,
};

const availabilityOptions = [
	{
		name: "紙本為下架",
		value: 0,
	},
	{
		name: "紙本為上架",
		value: 1,
	},
	{
		name: "線上測驗為上架",
		value: 2,
	},
	{
		name: "速測為上架",
		value: 3,
	},
];

const formatCheckListToPayload = (checkList) => {
	return checkList.reduce((acc, item) => {
		if (!item.eduSubject) return acc;
		const val = acc[item.eduSubject] ? [...acc[item.eduSubject], item.uid] : [item.uid];
		return {
			...acc,
			[item.eduSubject]: val,
		};
	}, {});
};
export const CheckListPage = () => {
	const [data, setData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const nowSchoolYear = getSchoolYear();
	const [selectionData, setSelectionData] = useState({});
	const [questionTypeOptions, setQuestionTypeOptions] = useState([]);

	const [searchParams, setSearchParams] = useState({
		checkStatus: [],
		year: nowSchoolYear + "",
		version: null,
		edu: null,
		subject: null,
		book: null,
		source: null,
		subSource: null,
		isChecked: null,
		fileName: "",
		userName: "",
		availability: null,
		questionTypeCode: null,
	});

	const goTop = () => {
		const scroll = document.querySelector(".CheckListTable > .body");
		scroll.scrollTop = 0;
	};

	// 翻頁相關功能

	const [pageState, setPageState] = useState(defaultPageState);

	const pageChange = (currentPage, pageSize) => {
		setPageState({
			...pageState,
			currentPage,
			limit: pageSize,
		});
		goTop();
	};

	const getCheckQuestion = async ({
		year,
		education,
		subjectCode,
		version,
		bookCode,
		checkStatus,
		CMS,
		questionTypeCode,
	}) => {
		try {
			setIsLoading(true);
			const { data, isSuccess, message } = await getCheckQuestionApi({
				year,
				education,
				subjectCode,
				version,
				bookCode,
				checkStatus,
				CMS,
				isPeriod: false,
				questionTypeCode,
			});

			if (isSuccess) {
				setPageState(defaultPageState);
				setData([...data]);
			} else {
				openNotificationWithIcon("error", message);
			}
		} catch (error) {
			openNotificationWithIcon("error", error.message);
		} finally {
			setIsLoading(false);
		}
	};

	const getQuerySeparateRecordSelection = async () => {
		const { data, isSuccess, message } = await getQuerySeparateRecordSelectionApi();
		const { bookMap, eduMap, eduSubject, eduVersion, sourceMap, subSourceMap, yearMap, volumeMap } = data;
		setSelectionData({ bookMap, eduMap, eduSubject, eduVersion, sourceMap, subSourceMap, yearMap, volumeMap });
		return data;
	};

	const getQuestionTypeSelection = async () => {
		try {
			const { data, isSuccess, message } = await getQuestionTypeSelectionApi();
			if (isSuccess) {
				setQuestionTypeOptions(setSelectOptions(data));
			} else {
				openNotificationWithIcon("error", message);
			}
		} catch (error) {
			openNotificationWithIcon("error", error.message);
		}
	};

	const { year, edu, subject, checkStatus, book, version, CMS, questionTypeCode } = searchParams;
	useEffect(() => {
		getQuestionTypeSelection();
		getQuerySeparateRecordSelection().then(() => {
			setSearchParams((prev) => ({
				...prev,
				edu: "E",
				subject: "CH",
				version: "N",
			}));
			return getCheckQuestion({
				year,
				education: "E",
				subjectCode: "CH",
				version: "N",
				bookCode: book,
				checkStatus,
				questionTypeCode,
			});
		});
	}, []);

	const searchHandler = () => {
		getCheckQuestion({
			year,
			education: edu,
			subjectCode: subject,
			version: version,
			bookCode: book,
			checkStatus,
			CMS,
			questionTypeCode,
		});
	};

	const filteredData = useMemo(
		() =>
			data.filter(({ availability }) => {
				const { paper, onlineTest, quick } = availability || {};
				switch (searchParams.availability) {
					case 0:
						return !paper;
					case 1:
						return paper;
					case 2:
						return onlineTest;
					case 3:
						return quick;
					default:
						return true;
				}
			}),
		[data, searchParams.availability],
	);

	const currentPageList = useMemo(() => {
		const start = (pageState.currentPage - 1) * pageState.limit;
		const end = start + pageState.limit;
		return filteredData.slice(start, end);
	}, [filteredData, pageState.currentPage, pageState.limit]);

	const { eduVersion, eduSubject } = selectionData;
	const subjectOptions = useMemo(() => {
		if (!eduSubject || !searchParams.edu || !eduSubject[searchParams.edu]) return [];
		return setSelectOptions(eduSubject[searchParams.edu]);
	}, [searchParams.edu]);

	const versionOptions = useMemo(() => {
		if (!eduVersion || !searchParams.edu || !eduVersion[searchParams.edu]) return [];
		return setSelectOptions(eduVersion[searchParams.edu]);
	}, [searchParams.edu]);

	const changeSearchParams = (key, value) => {
		if (key === "edu") {
			setSearchParams({
				...searchParams,
				[key]: value,
				subject: null,
				version: null,
				book: null,
			});
		} else if (key === "subject") {
			setSearchParams({
				...searchParams,
				[key]: value,
				book: null,
			});
		} else {
			setSearchParams({
				...searchParams,
				[key]: value,
			});
		}
	};

	const [checkList, setCheckedList] = useState([]);

	// 表格資料變動（重新搜尋、切換上架狀態篩選）時，避免送出已不在列表上的勾選項目
	useEffect(() => {
		setCheckedList([]);
	}, [filteredData]);

	const hasNoCheckedItem = checkList.length === 0;

	const checkHandler = (e, item) => {
		const checked = e.target.checked;
		let nextData = [...checkList];
		if (checked) {
			if (nextData.some((data) => data.cms === item.cms)) return;
			nextData = [...nextData, item];
			setCheckedList([...nextData]);
		} else {
			const dataIndex = nextData.findIndex((data) => data.cms === item.cms);
			nextData.splice(dataIndex, 1);
			setCheckedList([...nextData]);
		}
	};

	const isCheckAll = currentPageList.length > 0 && checkList.length === currentPageList.length;
	const indeterminate = !isCheckAll && checkList.length > 0 && checkList.length < currentPageList.length;

	const IsChecked = (item) => {
		if (checkList.length === 0) return false;
		return checkList.some((data) => data.cms === item.cms);
	};
	const checkListAll = (e) => {
		const checked = e.target.checked;
		if (checked) {
			setCheckedList([...currentPageList]);
		} else {
			setCheckedList([]);
		}
	};

	const [detailPopup, setDetailPopup] = useState({
		visible: false,
		fileUID: "",
		eduSubject: "",
	});

	const onDetailPopupHandle = ({ fileUID, eduSubject }) => {
		setDetailPopup({
			visible: true,
			fileUID,
			eduSubject,
		});
	};

	const onDetailCancel = () => {
		setDetailPopup({
			visible: false,
			fileUID: "",
			eduSubject: "",
		});
	};

	const checkedCount = useMemo(() => {
		return data.filter((item) => item.checkStatus === 10).length;
	}, [data]);

	const [finishedTask, setFinishedTask] = useState({
		visible: false,
	});

	const onFinishedTaskModalOk = async (value) => {
		try {
			const payload = formatCheckListToPayload(checkList);
			const updatedUids = new Set(Object.values(payload).flat());

			await updateQuestionCheckStatusApi(value, payload);

			setData((prev) => prev.map((item) => (updatedUids.has(item.uid) ? { ...item, checkStatus: value } : item)));
			setCheckedList([]);
		} catch (error) {
			openNotificationWithIcon("error", error.message);
		} finally {
			setFinishedTask({
				visible: false,
			});
		}
	};

	const onFinishedTaskModalCancel = () => {
		setFinishedTask({
			visible: false,
		});
	};

	const [checkExamLoading, setCheckExamLoading] = useState(false);
	const createExamToCheck = async ({ checkList }) => {
		try {
			setCheckExamLoading(true);
			const payload = formatCheckListToPayload(checkList);
			const res = await createCheckExamApi(payload);
			const { data, isSuccess } = res;
			if (isSuccess) {
				openNotificationWithIcon("success", "建立檢查測驗成功");
				window.open(`${process.env.REACT_APP_EXAM_DOMAIN}/internal/question-diagnostics?quizCode="${data.quizCode}"`);
			}
		} catch (error) {
			openNotificationWithIcon("error", error.message);
		} finally {
			setCheckExamLoading(false);
		}
	};

	const [downloadLoading, setDownloadLoading] = useState(false);
	const downloadQuestionList = async ({ checkList }) => {
		try {
			setDownloadLoading(true);
			const cmsNumbers = checkList.map((item) => item.cms);
			const { isSuccess, data } = await downloadCheckQuestionsApi(cmsNumbers);
			if (isSuccess) {
				window.open(data.url);
			} else {
				openNotificationWithIcon("error", "下載失敗");
			}
		} catch (error) {
			openNotificationWithIcon("error", error.message);
		} finally {
			setDownloadLoading(false);
		}
	};

	const [uploadModalVisible, setUploadModalVisible] = useState(false);
	const onUploadModalCancel = () => setUploadModalVisible(false);
	const openUploadModal = () => setUploadModalVisible(true);

	return (
		<StyledCheckListPage>
			<div className="flexStartBox">
				<Breadcrumb data={breadcrumbList} />
			</div>
			<div className="titleControlBox">
				<div className="selectionBar">
					<div className="checkStatusFilter">
						<PickListDropdown
							defaultTitle="顯示全部題目"
							options={filterTypeOptions}
							value={searchParams?.checkStatus || []}
							onChange={(val) => {
								changeSearchParams("checkStatus", val);
							}}
						/>
					</div>
					<Select
						placeholder="請選擇學年度"
						options={setSelectOptions(selectionData?.yearMap)}
						value={searchParams.year}
						onChange={(val) => {
							changeSearchParams("year", val);
						}}
						allowClear={false}
					/>
					<Select
						placeholder="請選擇學制"
						options={setSelectOptions(selectionData?.eduMap)}
						value={searchParams.edu}
						onChange={(val) => {
							changeSearchParams("edu", val);
						}}
					/>
					<Select
						placeholder="請選擇科目"
						options={subjectOptions}
						disabled={!searchParams?.edu}
						value={searchParams.subject}
						onChange={(val) => {
							changeSearchParams("subject", val);
						}}
					/>
					<Select
						placeholder="請選擇版本"
						options={versionOptions}
						disabled={!searchParams?.edu}
						value={searchParams.version}
						onChange={(val) => {
							changeSearchParams("version", val);
						}}
					/>
					<Select
						placeholder="請選擇冊次"
						options={setSelectOptions(selectionData?.volumeMap?.[searchParams.edu + searchParams.subject] || [])}
						value={searchParams.book}
						onChange={(val) => {
							changeSearchParams("book", val);
						}}
					/>
					<SearchSelect
						className="w220"
						placeholder="請選擇題型"
						options={questionTypeOptions}
						value={searchParams.questionTypeCode}
						onChange={(val) => {
							changeSearchParams("questionTypeCode", val ?? null);
						}}
					/>
					<Input
						placeholder="CMS編號"
						onChange={(e) => {
							changeSearchParams("CMS", e.target.value);
						}}
					/>
					<Select
						placeholder="上架狀態"
						options={availabilityOptions}
						value={searchParams.availability}
						onChange={(val) => {
							changeSearchParams("availability", val);
						}}
					/>
					<div>
						<Button variant="orange" onClick={searchHandler} loading={isLoading}>
							搜尋
						</Button>
					</div>
					{/* <Select
						placeholder="請選擇出處"
						options={setSelectOptions(selectionData?.sourceMap)}
						value={searchParams.source}
						onChange={(val) => {
							changeSearchParams("source", val);
						}}
					/>
					<Select
						placeholder="請選擇來源"
						options={setSelectOptions(selectionData?.subSourceMap)}
						value={searchParams.subSource}
						onChange={(val) => {
							changeSearchParams("subSource", val);
						}}
					/> */}
				</div>
			</div>
			<div className="countText">
				本冊已檢查/應有題量: {checkedCount} / {data.length}
			</div>
			<Table margin="3" className={"CheckListTable"}>
				<Table.Header className="header">
					<Table.Row>
						<Table.Item className="checkboxItem" flex="0.1">
							<Checkbox indeterminate={indeterminate} checked={isCheckAll} onChange={checkListAll} />
						</Table.Item>
						<Table.Item flex="0.5">CMS編號</Table.Item>
						<Table.Item flex="5">紙本截圖</Table.Item>
						<Table.Item className="availabilityItem" flex="2">
							上架狀態
						</Table.Item>
						<Table.Item flex="1">檢查狀態</Table.Item>
						<Table.Item flex="1">操作</Table.Item>
					</Table.Row>
				</Table.Header>
				<Table.Body className="body">
					{isLoading ? (
						<div className="loading">
							<Spin indicator={<Loading3QuartersOutlined spin={true} />} size="large" />
						</div>
					) : (
						currentPageList.map((item, index) => (
							<Table.Row key={item.cms + item.uid}>
								<Table.Item className="checkboxItem" flex="0.1">
									<Checkbox
										checked={IsChecked(item)}
										onChange={(event) => {
											checkHandler(event, item);
										}}
									/>
								</Table.Item>
								<Table.Item flex="0.5">{item.cms}</Table.Item>
								<Table.Item flex="5">
									<Image
										src={item.questionImage || "error"}
										preview={{
											getContainer: document.getElementById("quesTab"),
										}}
										fallback="data:image/png;base64,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"
									/>
								</Table.Item>
								<Table.Item className="availabilityItem" flex="2">
									<Availability {...(item.availability || {})} />
								</Table.Item>
								<Table.Item flex="1">
									<CheckedStatus status={item.checkStatus} />
								</Table.Item>
								<Table.Item flex="1">
									<Button.IconButton
										variant={item.questionImage ? "orange" : "ui01"}
										disabled={!item.questionImage}
										full={false}
										iconName="visibility"
										onClick={() => onDetailPopupHandle(item)}>
										看更多
									</Button.IconButton>
								</Table.Item>
							</Table.Row>
						))
					)}
				</Table.Body>
			</Table>
			<div className="buttonGroup">
				<div className="btn">
					<Button.IconButton
						variant="blue"
						full={false}
						iconName="download"
						disabled={hasNoCheckedItem}
						onClick={() => downloadQuestionList({ checkList })}
						loading={downloadLoading}>
						批次下載
					</Button.IconButton>
				</div>
				{/* <div className="btn">
					<Button.IconButton variant="blue" full={false} iconName="upload" onClick={openUploadModal}>
						修改試題
					</Button.IconButton>
				</div> */}
				<div className="btn">
					<Button.IconButton
						variant="blue"
						full={false}
						iconName="check"
						disabled={hasNoCheckedItem}
						onClick={() =>
							setFinishedTask({
								visible: true,
							})
						}>
						完成作業
					</Button.IconButton>
				</div>
				<div className="btn">
					<Button.IconButton
						variant="orange"
						full={false}
						iconName="visibility"
						disabled={hasNoCheckedItem}
						onClick={() => createExamToCheck({ checkList })}
						loading={checkExamLoading}>
						檢查測驗
					</Button.IconButton>
				</div>
			</div>
			<Pagination
				total={filteredData.length || 0}
				defaultCurrent={1}
				current={pageState.currentPage}
				onChange={pageChange}
				defaultPageSize={pageState.limit}
			/>
			<UiPopupContainer id="quesTab" />
			{detailPopup.visible && (
				<QuestDetail fileUID={detailPopup.fileUID} eduSubject={detailPopup.eduSubject} onCancel={onDetailCancel} />
			)}
			{finishedTask.visible && (
				<FinishedTaskModal
					open={finishedTask.visible}
					onOk={onFinishedTaskModalOk}
					onCancel={onFinishedTaskModalCancel}
				/>
			)}
			<UploadModal onCancel={onUploadModalCancel} visible={uploadModalVisible} />
		</StyledCheckListPage>
	);
};

const AvailabilityIcon = ({ isDone }) =>
	isDone ? <Icon name="done" color="green" /> : <Icon name="close" color="red" />;

// 線上測驗 紙本 速測 可以上架(?)
function Availability({
	paper,
	onlineTest,
	quick,
	equationDojo,
	freeProduct,
	selfTaughtProduct,
	mockExam,
	collectedEdition,
	validation,
	optionQuick,
	vocabularyTest,
}) {
	return (
		<StyledAvailability>
			<p>
				紙本上架狀態
				<AvailabilityIcon isDone={paper} />
			</p>
			<p>
				速測上架狀態
				<AvailabilityIcon isDone={quick} />
			</p>
			<p>
				線上測驗上架狀態 <AvailabilityIcon isDone={onlineTest} />
			</p>
			<p>
				無限命題上架狀態 <AvailabilityIcon isDone={equationDojo} />
			</p>
			<p>
				免費產品上架狀態 <AvailabilityIcon isDone={freeProduct} />
			</p>
			<p>
				自學產品上架狀態 <AvailabilityIcon isDone={selfTaughtProduct} />
			</p>
			<p>
				模擬考上架狀態 <AvailabilityIcon isDone={mockExam} />
			</p>
			<p>
				叢書成卷上架狀態 <AvailabilityIcon isDone={collectedEdition} />
			</p>
			<p>
				檢定試題上架狀態 <AvailabilityIcon isDone={validation} />
			</p>
			<p>
				特殊選項試題上架狀態 <AvailabilityIcon isDone={optionQuick} />
			</p>

			<p>
				單字刷題試題上架狀態 <AvailabilityIcon isDone={vocabularyTest} />
			</p>
		</StyledAvailability>
	);
}

function CheckedStatus({ status }) {
	const statusMap = {
		0: "未檢查",
		1: "待檢查",
		2: "速測待檢查",
		3: "紙本待檢查",
		4: "待教科修題",
		5: "待南編修題",
		10: "完成檢查",
	};

	return <Tag color="#108ee9">{statusMap[status]}</Tag>;
}
